import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  getEventInstructors,
  listAvailableInstructors,
  createInstructorProfile,
  uploadInstructorImage,
  addInstructorToEvent,
  removeInstructorFromEvent,
  setPrimaryInstructor
} from '../../services/instructorsService';
import { useAuth } from '../../contexts/AuthContext';
import { getPublicImageUrl } from '../../utils/storage';

const { FiPlus, FiEdit2, FiTrash2, FiUser, FiMapPin, FiBriefcase, FiStar, FiX } = FiIcons;

export default function InstructorsTab({ eventId, onUpdate }) {
  const { user } = useAuth();
  const [eventInstructors, setEventInstructors] = useState([]);
  const [availableInstructors, setAvailableInstructors] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [eventId]);

  async function loadData() {
    try {
      setLoading(true);
      const [eventInstResult, availableResult] = await Promise.all([
        getEventInstructors(eventId),
        listAvailableInstructors(eventId)
      ]);

      if (eventInstResult.error) {
        console.error('[InstructorsTab] Error loading event instructors:', eventInstResult.error);
      }
      if (availableResult.error) {
        console.error('[InstructorsTab] Error loading available instructors:', availableResult.error);
      }

      setEventInstructors(eventInstResult.data || []);
      setAvailableInstructors(availableResult.data || []);
    } catch (error) {
      console.error('[InstructorsTab] Error loading instructors:', error);
      toast.error('Failed to load instructors');
    } finally {
      setLoading(false);
    }
  }

  async function handleAddExisting(instructorId, role, isPrimary) {
    try {
      const { error } = await addInstructorToEvent(eventId, instructorId, role, isPrimary);
      if (error) throw error;

      await loadData();
      setShowAddModal(false);
      toast.success('Instructor added successfully');
      onUpdate?.();
    } catch (error) {
      console.error('[InstructorsTab] Error adding instructor:', error);
      toast.error('Failed to add instructor');
    }
  }

  async function handleRemove(instructorId) {
    if (!window.confirm('Remove this instructor from the event?')) return;

    try {
      const { error } = await removeInstructorFromEvent(eventId, instructorId);
      if (error) throw error;

      await loadData();
      toast.success('Instructor removed');
      onUpdate?.();
    } catch (error) {
      console.error('[InstructorsTab] Error removing instructor:', error);
      toast.error('Failed to remove instructor');
    }
  }

  async function handleSetPrimary(instructorId) {
    try {
      const { error } = await setPrimaryInstructor(eventId, instructorId);
      if (error) throw error;

      await loadData();
      toast.success('Primary instructor updated');
      onUpdate?.();
    } catch (error) {
      console.error('[InstructorsTab] Error updating primary instructor:', error);
      toast.error('Failed to update primary instructor');
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Event Instructors</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage instructors for this event
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
            Create New
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
            Add Existing
          </button>
        </div>
      </div>

      {/* Instructors List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {eventInstructors.length === 0 ? (
          <div className="md:col-span-2 text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <SafeIcon icon={FiUser} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No instructors added yet</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Add your first instructor
            </button>
          </div>
        ) : (
          eventInstructors.map((instructor) => (
            <div key={instructor.instructor_id} className="bg-white rounded-lg shadow-sm p-6 relative">
              {instructor.is_primary && (
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <SafeIcon icon={FiStar} className="w-3 h-3 mr-1" />
                    Primary
                  </span>
                </div>
              )}

              <div className="flex items-start space-x-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {instructor.profile_image_url ? (
                    <img
                      src={getPublicImageUrl(instructor.profile_image_url, 'instructor-images')}
                      alt={instructor.full_name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="w-8 h-8 text-blue-600" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {instructor.full_name}
                  </h3>

                  {instructor.title && (
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <SafeIcon icon={FiBriefcase} className="w-4 h-4 mr-1" />
                      {instructor.title}
                    </div>
                  )}

                  {(instructor.city || instructor.state) && (
                    <div className="flex items-center text-sm text-gray-600 mb-3">
                      <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-1" />
                      {[instructor.city, instructor.state].filter(Boolean).join(', ')}
                    </div>
                  )}

                  {instructor.bio && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {instructor.bio}
                    </p>
                  )}

                  <div className="flex items-center space-x-2">
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                      {instructor.role || 'instructor'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-4 pt-4 border-t flex items-center justify-end space-x-2">
                {!instructor.is_primary && (
                  <button
                    onClick={() => handleSetPrimary(instructor.instructor_id)}
                    className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded hover:bg-gray-100"
                  >
                    Set as Primary
                  </button>
                )}
                <button
                  onClick={() => handleRemove(instructor.instructor_id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Remove instructor"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Existing Modal */}
      {showAddModal && (
        <AddExistingInstructorModal
          instructors={availableInstructors}
          onAdd={handleAddExisting}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Create New Modal */}
      {showCreateModal && (
        <CreateInstructorModal
          eventId={eventId}
          onSuccess={() => {
            setShowCreateModal(false);
            loadData();
          }}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
}

// Add Existing Instructor Modal
function AddExistingInstructorModal({ instructors, onAdd, onClose }) {
  const [selectedInstructor, setSelectedInstructor] = useState('');
  const [role, setRole] = useState('instructor');
  const [isPrimary, setIsPrimary] = useState(false);
  const [adding, setAdding] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedInstructor) {
      toast.error('Please select an instructor');
      return;
    }

    setAdding(true);
    await onAdd(selectedInstructor, role, isPrimary);
    setAdding(false);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 my-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Add Existing Instructor</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Instructor *
            </label>
            {instructors.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No available instructors to add. Create instructor profiles in Account Profile, or set existing profiles to Public.</p>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-2">
                {instructors.map((inst) => (
                  <button
                    key={inst.id}
                    type="button"
                    onClick={() => setSelectedInstructor(inst.id)}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${selectedInstructor === inst.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'}`}
                  >
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0 flex items-center justify-center">
                      {getPublicImageUrl(inst.profile_image_url, 'instructor-images') ? (
                        <img src={getPublicImageUrl(inst.profile_image_url, 'instructor-images')} alt={inst.full_name} className="w-full h-full object-cover" />
                      ) : (
                        <SafeIcon icon={FiUser} className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{inst.full_name}</p>
                      {inst.title && <p className="text-xs text-gray-500 truncate">{inst.title}</p>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="lead_instructor">Lead Instructor</option>
              <option value="instructor">Instructor</option>
              <option value="co_instructor">Co-Instructor</option>
              <option value="guest_speaker">Guest Speaker</option>
              <option value="moderator">Moderator</option>
            </select>
          </div>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Set as primary instructor</span>
          </label>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={adding || instructors.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {adding ? 'Adding...' : 'Add Instructor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Create New Instructor Modal
function CreateInstructorModal({ eventId, onSuccess, onClose }) {
  const { user } = useAuth();
  const imageInputRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: '',
    title: '',
    bio: '',
    profile_image_url: '',
    specializations: '',
    website_url: '',
    linkedin_url: '',
    visibility: 'private'
  });
  const [creating, setCreating] = useState(false);

  async function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    const { data, error } = await uploadInstructorImage(file, user.id);
    if (error) {
      toast.error(error.message ?? 'Upload failed');
      return;
    }
    const pathOnly = data?.path || data?.fullPath;
    if (pathOnly) setFormData((prev) => ({ ...prev, profile_image_url: pathOnly }));
    toast.success('Image selected');
    e.target.value = '';
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.full_name.trim()) {
      toast.error('Name is required');
      return;
    }

    setCreating(true);
    try {
      const { error } = await createInstructorProfile({
        full_name: formData.full_name.trim(),
        title: formData.title.trim() || undefined,
        bio: formData.bio.trim() || undefined,
        profile_image_url: formData.profile_image_url || undefined,
        linkedin_url: formData.linkedin_url.trim() || undefined,
        website_url: formData.website_url.trim() || undefined,
        visibility: formData.visibility || 'private',
        specializations: formData.specializations
          ? formData.specializations.split(',').map((s) => s.trim()).filter(Boolean)
          : []
      });

      if (error) throw error;

      toast.success('Instructor created successfully');
      onSuccess();
    } catch (error) {
      console.error('[CreateInstructorModal] Error:', error);
      toast.error(error?.message ?? 'Failed to create instructor');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 my-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Create New Instructor</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Profile image
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  className="w-20 h-20 rounded-xl bg-gray-100 border-2 border-dashed border-gray-300 hover:border-blue-500 flex items-center justify-center overflow-hidden flex-shrink-0"
                >
                  {getPublicImageUrl(formData.profile_image_url, 'instructor-images') ? (
                    <img src={getPublicImageUrl(formData.profile_image_url, 'instructor-images')} alt="Instructor" className="w-full h-full object-cover" />
                  ) : (
                    <SafeIcon icon={FiUser} className="w-8 h-8 text-gray-400" />
                  )}
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
                <span className="text-xs text-gray-500">Click to upload (instructor-images)</span>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Senior Financial Advisor"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bio
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Brief biography..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specializations (comma-separated)
              </label>
              <input
                type="text"
                value={formData.specializations}
                onChange={(e) => setFormData({ ...formData, specializations: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Retirement Planning, Tax Strategy"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website URL
              </label>
              <input
                type="url"
                value={formData.website_url}
                onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LinkedIn URL
              </label>
              <input
                type="url"
                value={formData.linkedin_url}
                onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Instructor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
